import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Sparkles, Check, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Voice dictation flow for clinical evolutions AND for the full AGA form.
 *
 * `mode`:
 *   - "evolution" (default): split into the 4 evolution fields (chief_complaint,
 *     disease_history, important_notes, therapeutic_proposal).
 *   - "aga": distribute into AGA textarea/text fields grouped by section
 *     (identificacao, antecedentes, exame_fisico, impressao_conduta, ...).
 *     onApply receives {section_id: {field_key: text}} and the callback is
 *     responsible for merging into assessment.data.
 */
export default function VoiceDictateButton({ onApply, disabled, mode = "evolution", label, sectionId, compact = false }) {
    const [open, setOpen] = useState(false);
    const btnLabel = label || (mode === "aga" ? (sectionId ? "Ditar seção" : "Ditar avaliação") : "Ditar evolução");
    const btnClasses = compact
        ? "text-xs px-2 py-1 rounded-md text-brand border border-brand/60 hover:bg-alt transition-colors flex items-center gap-1 disabled:opacity-50"
        : "text-sm px-3 py-1.5 rounded-lg text-brand border border-brand hover:bg-alt transition-colors flex items-center gap-1.5 disabled:opacity-50";
    return (
        <>
            <button
                type="button"
                data-testid={sectionId ? `voice-dictate-section-${sectionId}` : "voice-dictate-open"}
                disabled={disabled}
                onClick={() => setOpen(true)}
                className={btnClasses}
                title={mode === "aga"
                    ? (sectionId
                        ? "Grave somente esta seção — a IA distribui apenas nos campos daqui."
                        : "Grave a avaliação por voz — a IA distribui nos campos da AGA (motivo, HDA, antecedentes, exame físico, conduta…) e você revisa antes de aplicar.")
                    : "Grave a evolução por voz — a IA distribui nos campos e você revisa antes de salvar."}
            >
                <Mic size={compact ? 12 : 14} /> {btnLabel}
            </button>
            {open && (
                <DictateModal
                    mode={mode}
                    sectionId={sectionId}
                    onClose={() => setOpen(false)}
                    onApply={(payload) => {
                        onApply(payload);
                        setOpen(false);
                    }}
                />
            )}
        </>
    );
}

const STAGES = {
    IDLE: "idle",
    RECORDING: "recording",
    UPLOADING: "uploading",
    STRUCTURING: "structuring",
    REVIEW: "review",
    ERROR: "error",
};

const SECTION_TITLES = {
    identificacao: "Identificação / Motivo / HDA",
    antecedentes: "Antecedentes Pessoais",
    social_estilo_vida: "História de Vida / Estilo de Vida",
    vacinacao: "Vacinação",
    funcionalidade: "Funcionalidade (Katz / Lawton)",
    cognicao_humor_nutricao: "Cognição / Humor / Nutrição",
    npi_q: "NPI-Q — Neuropsiquiátrico",
    exame_fisico: "Exame Físico",
    impressao_conduta: "Impressão & Conduta",
};

function DictateModal({ mode = "evolution", sectionId, onClose, onApply }) {
    const [stage, setStage] = useState(STAGES.IDLE);
    const [errorMsg, setErrorMsg] = useState(null);
    const [seconds, setSeconds] = useState(0);
    const [transcript, setTranscript] = useState("");
    // Evolution mode: 4 flat fields
    const [fields, setFields] = useState({
        chief_complaint: "",
        disease_history: "",
        important_notes: "",
        therapeutic_proposal: "",
    });
    // AGA mode: {section_id: {field_key: text}}
    const [agaSections, setAgaSections] = useState({});
    const [agaFieldMap, setAgaFieldMap] = useState({});

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        return () => {
            // Cleanup: stop any active recording / stream if user closes mid-way
            if (timerRef.current) clearInterval(timerRef.current);
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                try { mediaRecorderRef.current.stop(); } catch { /* noop */ }
            }
            if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        };
    }, []);

    const pickMimeType = () => {
        if (typeof MediaRecorder === "undefined") return "";
        const candidates = [
            "audio/webm;codecs=opus",
            "audio/webm",
            "audio/mp4",
            "audio/ogg;codecs=opus",
        ];
        for (const t of candidates) {
            if (MediaRecorder.isTypeSupported(t)) return t;
        }
        return "";
    };

    const startRecording = async () => {
        setErrorMsg(null);
        if (!navigator.mediaDevices || !window.MediaRecorder) {
            setStage(STAGES.ERROR);
            setErrorMsg("Seu navegador não suporta gravação de áudio. Use Chrome, Edge ou Safari recente.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = pickMimeType();
            const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            mediaRecorderRef.current = mr;
            chunksRef.current = [];
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            mr.onstop = () => {
                const type = mr.mimeType || "audio/webm";
                const blob = new Blob(chunksRef.current, { type });
                uploadAndTranscribe(blob, type);
                if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            };
            mr.start();
            setStage(STAGES.RECORDING);
            setSeconds(0);
            timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
        } catch (e) {
            setStage(STAGES.ERROR);
            setErrorMsg("Não foi possível acessar o microfone. Verifique a permissão do navegador.");
        }
    };

    const stopRecording = () => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") {
            try { mr.stop(); } catch { /* noop */ }
        }
    };

    const callSplit = async (text) => {
        if (mode === "aga") {
            const body = sectionId ? { transcript: text, section_id: sectionId } : { transcript: text };
            const s = await api.post("/dictation/split-aga", body, { timeout: 60000 });
            setAgaSections(s.data?.sections || {});
            setAgaFieldMap(s.data?.field_map || {});
        } else {
            const s = await api.post("/dictation/split-evolution", { transcript: text }, { timeout: 60000 });
            setFields({
                chief_complaint: s.data?.chief_complaint || "",
                disease_history: s.data?.disease_history || "",
                important_notes: s.data?.important_notes || "",
                therapeutic_proposal: s.data?.therapeutic_proposal || "",
            });
        }
    };

    const uploadAndTranscribe = async (blob, mimeType) => {
        setStage(STAGES.UPLOADING);
        try {
            const ext = mimeType.includes("mp4") ? "mp4" : mimeType.includes("ogg") ? "ogg" : "webm";
            const fd = new FormData();
            fd.append("audio", blob, `evolucao.${ext}`);
            const t = await api.post("/dictation/transcribe", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            const text = (t.data?.text || "").trim();
            setTranscript(text);
            if (!text) {
                setStage(STAGES.ERROR);
                setErrorMsg("Nenhum áudio foi captado. Verifique o microfone e tente novamente.");
                return;
            }
            setStage(STAGES.STRUCTURING);
            await callSplit(text);
            setStage(STAGES.REVIEW);
        } catch (e) {
            setStage(STAGES.ERROR);
            setErrorMsg(formatApiError(e));
        }
    };

    const reRunStructure = async () => {
        if (!transcript.trim()) return;
        setStage(STAGES.STRUCTURING);
        try {
            await callSplit(transcript);
            setStage(STAGES.REVIEW);
        } catch (e) {
            setStage(STAGES.ERROR);
            setErrorMsg(formatApiError(e));
        }
    };

    const restart = () => {
        setTranscript("");
        setFields({ chief_complaint: "", disease_history: "", important_notes: "", therapeutic_proposal: "" });
        setAgaSections({});
        setErrorMsg(null);
        setStage(STAGES.IDLE);
    };

    const apply = () => {
        if (mode === "aga") {
            const hasAny = Object.values(agaSections).some((sec) =>
                Object.values(sec || {}).some((v) => (v || "").trim())
            );
            if (!hasAny) { toast.error("Nenhum conteúdo para aplicar."); return; }
            onApply({ sections: agaSections });
            toast.success("Avaliação distribuída. Confira cada seção na aba AGA.");
            return;
        }
        const hasAny = Object.values(fields).some((v) => (v || "").trim());
        if (!hasAny) {
            toast.error("Nenhum conteúdo para aplicar.");
            return;
        }
        onApply(fields);
        toast.success("Campos preenchidos. Revise e clique em Registrar para salvar.");
    };

    const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="dictate-modal">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-5 border-b border-[var(--border-organic)] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Mic size={20} className="text-brand" />
                        <h2 className="font-display text-xl font-medium text-brand">
                            {mode === "aga" ? "Ditado por voz — Avaliação Geriátrica" : "Ditado por voz — Evolução"}
                        </h2>
                    </div>
                    <button onClick={onClose} data-testid="dictate-close" className="text-muted-clinical hover:text-brand"><X size={20} /></button>
                </div>

                <div className="p-5 space-y-4">
                    {stage === STAGES.IDLE && (
                        <div className="text-center py-8 space-y-4" data-testid="dictate-stage-idle">
                            <p className="text-sm text-muted-clinical max-w-md mx-auto">
                                Clique em <strong>Gravar</strong> e fale livremente. A IA transcreve em português e distribui automaticamente
                                {mode === "aga"
                                    ? " nos campos da AGA (motivo, HDA, antecedentes, exame físico, conduta…)"
                                    : " nos 4 campos de evolução"}.
                                Você poderá revisar cada campo antes de aplicar.
                            </p>
                            <button
                                onClick={startRecording}
                                data-testid="dictate-start-btn"
                                className="mx-auto flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-white hover:bg-brand-dark transition-colors font-medium"
                            >
                                <Mic size={18} /> Gravar
                            </button>
                        </div>
                    )}

                    {stage === STAGES.RECORDING && (
                        <div className="text-center py-8 space-y-4" data-testid="dictate-stage-recording">
                            <div className="mx-auto w-20 h-20 rounded-full bg-[var(--status-danger-bg)] flex items-center justify-center relative">
                                <Mic size={32} className="text-[var(--status-danger)]" />
                                <span className="absolute inset-0 rounded-full border-4 border-[var(--status-danger)] animate-ping opacity-40" />
                            </div>
                            <p className="text-2xl font-mono text-brand" data-testid="dictate-timer">{fmtTime(seconds)}</p>
                            <p className="text-sm text-muted-clinical">Gravando… fale próximo ao microfone.</p>
                            <button
                                onClick={stopRecording}
                                data-testid="dictate-stop-btn"
                                className="mx-auto flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--status-danger)] text-white hover:opacity-90 transition-opacity font-medium"
                            >
                                <Square size={16} /> Parar
                            </button>
                        </div>
                    )}

                    {(stage === STAGES.UPLOADING || stage === STAGES.STRUCTURING) && (
                        <div className="text-center py-10 space-y-3" data-testid="dictate-stage-processing">
                            <Loader2 size={32} className="animate-spin text-brand mx-auto" />
                            <p className="text-sm text-brand">
                                {stage === STAGES.UPLOADING ? "Transcrevendo áudio (Whisper)…" : "Organizando nos campos (Claude Sonnet 4.5)…"}
                            </p>
                        </div>
                    )}

                    {stage === STAGES.REVIEW && (
                        <div className="space-y-3" data-testid="dictate-stage-review">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <p className="text-sm text-brand-secondary flex items-center gap-1.5">
                                    <Sparkles size={14} className="text-brand" />
                                    Revise e edite cada campo. Nada será salvo até você clicar em Aplicar.
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={reRunStructure}
                                        data-testid="dictate-rerun-btn"
                                        className="text-xs px-2.5 py-1 rounded-lg text-brand border border-brand hover:bg-alt flex items-center gap-1"
                                        title="Reprocessar a mesma transcrição"
                                    >
                                        <RotateCcw size={12} /> Reorganizar
                                    </button>
                                    <button
                                        onClick={restart}
                                        data-testid="dictate-restart-btn"
                                        className="text-xs px-2.5 py-1 rounded-lg text-brand-secondary border border-[var(--border-organic)] hover:bg-alt flex items-center gap-1"
                                    >
                                        <Mic size={12} /> Gravar de novo
                                    </button>
                                </div>
                            </div>

                            {mode === "aga" ? (
                                Object.keys(agaSections).length === 0 ? (
                                    <div className="rounded-lg border border-dashed border-[var(--border-organic)] p-5 text-center text-sm text-muted-clinical" data-testid="dictate-aga-empty">
                                        A IA não identificou campos preenchíveis. Grave novamente com mais detalhes
                                        (ex: "paciente queixa-se de insônia, uso de melatonina 3mg, PA 130x80, impressão: insônia crônica, retorno em 30 dias").
                                    </div>
                                ) : (
                                    Object.entries(agaSections).map(([sectionId, sectionFields]) => (
                                        <div key={sectionId} className="rounded-lg border border-[var(--border-organic)] bg-alt/30 p-3 space-y-2" data-testid={`dictate-aga-section-${sectionId}`}>
                                            <div className="text-xs uppercase tracking-[0.14em] text-brand-secondary font-semibold">
                                                {SECTION_TITLES[sectionId] || sectionId}
                                            </div>
                                            {Object.entries(sectionFields).map(([fieldKey, value]) => (
                                                <FieldEditor
                                                    key={fieldKey}
                                                    testId={`dictate-aga-${sectionId}-${fieldKey}`}
                                                    label={(agaFieldMap[sectionId] && agaFieldMap[sectionId][fieldKey]) || fieldKey}
                                                    rows={Math.max(2, Math.ceil((value || "").length / 90))}
                                                    value={value}
                                                    onChange={(v) => setAgaSections({
                                                        ...agaSections,
                                                        [sectionId]: { ...sectionFields, [fieldKey]: v },
                                                    })}
                                                />
                                            ))}
                                        </div>
                                    ))
                                )
                            ) : (
                                <>
                                    <FieldEditor
                                        testId="dictate-field-chief"
                                        label="Queixa principal"
                                        rows={2}
                                        value={fields.chief_complaint}
                                        onChange={(v) => setFields({ ...fields, chief_complaint: v })}
                                    />
                                    <FieldEditor
                                        testId="dictate-field-history"
                                        label="História da doença"
                                        rows={3}
                                        value={fields.disease_history}
                                        onChange={(v) => setFields({ ...fields, disease_history: v })}
                                    />
                                    <FieldEditor
                                        testId="dictate-field-notes"
                                        label="Anotações importantes"
                                        rows={3}
                                        value={fields.important_notes}
                                        onChange={(v) => setFields({ ...fields, important_notes: v })}
                                    />
                                    <FieldEditor
                                        testId="dictate-field-therapeutic"
                                        label="Proposta terapêutica"
                                        rows={3}
                                        value={fields.therapeutic_proposal}
                                        onChange={(v) => setFields({ ...fields, therapeutic_proposal: v })}
                                    />
                                </>
                            )}

                            {transcript && (
                                <details className="text-xs text-muted-clinical">
                                    <summary className="cursor-pointer hover:text-brand">Ver transcrição bruta</summary>
                                    <p className="mt-2 p-2 bg-alt rounded whitespace-pre-wrap" data-testid="dictate-raw-transcript">{transcript}</p>
                                </details>
                            )}
                        </div>
                    )}

                    {stage === STAGES.ERROR && (
                        <div className="text-center py-8 space-y-3" data-testid="dictate-stage-error">
                            <p className="text-sm text-[var(--status-danger)] font-medium">{errorMsg || "Ocorreu um erro."}</p>
                            <button
                                onClick={restart}
                                className="btn-secondary mx-auto"
                                data-testid="dictate-retry-btn"
                            >
                                <RotateCcw size={14} /> Tentar de novo
                            </button>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-[var(--border-organic)] flex justify-end gap-2">
                    <button onClick={onClose} className="btn-secondary" data-testid="dictate-cancel-btn">Cancelar</button>
                    {stage === STAGES.REVIEW && (
                        <button onClick={apply} className="btn-primary" data-testid="dictate-apply-btn">
                            <Check size={14} /> Aplicar aos campos
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function FieldEditor({ label, value, onChange, rows, testId }) {
    return (
        <div>
            <label className="label-clinical">{label}</label>
            <textarea
                data-testid={testId}
                rows={rows}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="input-clinical text-sm"
            />
        </div>
    );
}
